import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField, IconButton, Tooltip } from '@mui/material';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3.5,
  maxHeight: '80vh',
  overflowY: 'auto',
};

export default function DQAgentCustomRuleModal({ open, handleClose, columnName = [] }) {
  const INITIALSTATE = {
    fieldName: '',
    firstSampleValue: '',
    secondSampleValue: '',
    statement: '',
    targetColumnName: '',
    dynamicRows: [{ fieldName: '', firstSampleValue: '', secondSampleValue: '' }],
  };

  const [formField, setFormField] = useState(INITIALSTATE);
  const [errors, setErrors] = useState({});

  const handleChange = (event, source, index) => {
    setErrors({});
    switch (source) {
      case 'fieldName': {
        const updatedRows = [...formField.dynamicRows];
        updatedRows[index].fieldName = event.target.value;
        setFormField({ ...formField, dynamicRows: updatedRows });
        break;
      }
      case 'firstSampleValue': {
        const updatedRows = [...formField.dynamicRows];
        updatedRows[index].firstSampleValue = event.target.value;
        setFormField({ ...formField, dynamicRows: updatedRows });
        break;
      }
      case 'secondSampleValue': {
        const updatedRows = [...formField.dynamicRows];
        updatedRows[index].secondSampleValue = event.target.value;
        setFormField({ ...formField, dynamicRows: updatedRows });
        break;
      }
      case 'statement': {
        setFormField({ ...formField, statement: event.target.value });
        break;
      }
      case 'targetColumnName': {
        setFormField({ ...formField, targetColumnName: event.target.value });
        break;
      }
      default:
        break;
    }
  };

  const handleCancel = () => {
    setFormField(INITIALSTATE);
    setErrors({});
    handleClose();
  };

  const handleAddRow = () => {
    setFormField({
      ...formField,
      dynamicRows: [...formField.dynamicRows, { fieldName: '', firstSampleValue: '', secondSampleValue: '' }],
    });
  };

  const handleDeleteRow = (index) => {
    if (formField.dynamicRows.length === 1) {
      setErrors({ ...errors, dynamicRows: 'At least one row is required' });
      return;
    }
    const updatedRows = formField.dynamicRows.filter((_, i) => i !== index);
    setFormField({ ...formField, dynamicRows: updatedRows });
  };

  const validateForm = () => {
    const newErrors = {};

    if (formField.dynamicRows.length === 0) {
      newErrors.dynamicRows = 'At least one row is required';
    }

    formField.dynamicRows.forEach((row, index) => {
      if (!row.fieldName) {
        newErrors[`fieldName-${index}`] = 'Column Name is required';
      }
      if (!row.firstSampleValue) {
        newErrors[`firstSampleValue-${index}`] = 'Value is required';
      }
      if (!row.secondSampleValue) {
        newErrors[`secondSampleValue-${index}`] = 'Value is required';
      }
    });

    if (!formField.targetColumnName && formField.dynamicRows.length > 1) {
      newErrors.targetColumnName = 'Target Column Name is required';
    }

    if (!formField.statement) {
      newErrors.statement = 'Statement is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddCustomRule = async () => {
    if (!validateForm()) return;

    const targetColumn =
      formField.dynamicRows.length === 1 ? formField.dynamicRows[0].fieldName : formField.targetColumnName;

    const data = {
      dynamicRows: formField.dynamicRows,
      statement: formField.statement,
      targetColumnName: targetColumn,
    };

    handleClose();
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box
            sx={{
              borderBottom: '1px solid #e0e0e0',
              pb: 2,
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" component="h2" sx={{ fontWeight: '600', color: '#00043C' }}>
              Custom Rule
            </Typography>
            <Tooltip
              title="This block allows you to create custom validation rules by defining dependencies between columns and specifying the validation logic."
              arrow
            >
              <IconButton>
                <InfoIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            {formField.dynamicRows.map((row, index) => (
              <Grid container spacing={2} alignItems="center" key={index} sx={{ mt: 1 }}>
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth size="small" error={!!errors[`fieldName-${index}`]}>
                    <InputLabel>Column Name</InputLabel>
                    <Select
                      value={row.fieldName}
                      onChange={(e) => handleChange(e, 'fieldName', index)}
                      label="Column Name"
                      sx={{ background: '#ffffff' }}
                    >
                      {columnName.map((item) => (
                        <MenuItem key={item.id} value={item.label}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors[`fieldName-${index}`] && (
                      <Typography variant="caption" color="error">
                        {errors[`fieldName-${index}`]}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={1}>
                  <IconButton
                    onClick={() => handleDeleteRow(index)}
                    sx={{ color: '#ff4444', '&:hover': { color: '#cc0000' } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            {errors.dynamicRows && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.dynamicRows}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              sx={{
                textTransform: 'none',
                color: '#00043C',
                borderColor: '#00043C',
                '&:hover': {
                  backgroundColor: '#00043C',
                  color: '#ffffff',
                  borderColor: '#00043C',
                },
              }}
            >
              Add Dependent Column
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Statement"
                value={formField.statement}
                onChange={(e) => handleChange(e, 'statement')}
                error={!!errors.statement}
                helperText={errors.statement}
              />
            </Grid>
            {formField.dynamicRows.length > 1 && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" error={!!errors.targetColumnName}>
                  <InputLabel>Target Column Name</InputLabel>
                  <Select
                    value={formField.targetColumnName}
                    onChange={(e) => handleChange(e, 'targetColumnName')}
                    label="Target Column Name"
                    sx={{ background: '#ffffff' }}
                  >
                    {formField.dynamicRows.map((row, index) => (
                      <MenuItem key={index} value={row.fieldName}>
                        {row.fieldName}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.targetColumnName && (
                    <Typography variant="caption" color="error">
                      {errors.targetColumnName}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            )}
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                textTransform: 'none',
                color: '#00043C',
                borderColor: '#00043C',
                '&:hover': {
                  backgroundColor: '#00043C',
                  color: '#ffffff',
                  borderColor: '#00043C',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddCustomRule}
              sx={{
                textTransform: 'none',
                backgroundColor: '#00043C',
                '&:hover': { backgroundColor: '#00043C' },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
